import { useQuery } from '@tanstack/react-query'
import type { ExportStatus, ReportFormat, ReportKind, ScoreStats } from '@concours/shared'
import { useAuth } from '@concours/ui'

/** Tableaux de bord et exports (phase 19). */
export const REPORTS_KEY = ['admin', 'reports'] as const

export interface Count {
  key: string
  label: string
  count: number
}

export interface StageReport {
  id: string
  competition: { id: string; reference: string }
  label: string
  position: number
  status: string
  statusLabel: string
  population: number
  present: number
  absent: number
  participation: number | null
  seats: number | null
  ranking: { version: number; status: string } | null
  outcome: { passed: number; supplementary: number; notRetained: number; pending: number }
  ties: number
  disciplines: (ScoreStats & { label: string; maxScore: number | null; absent: number })[]
}

export interface Overview {
  competitions: { id: string; reference: string; title: string }[]
  accounts: { total: number; last30: number; active: number } | null
  applications: {
    started: number
    submitted: number
    admissible: number
    notAdmissible: number
    toComplete: number
    underReview: number
    withdrawn: number
    byStatus: Count[]
    byCompetition: {
      id: string
      reference: string
      title: string
      seats: number | null
      started: number
      submitted: number
      admissible: number
      notAdmissible: number
    }[]
  }
  distributions: { gender: Count[]; region: Count[]; category: Count[] }
  payments: {
    pending: number
    confirmed: number
    failed: number
    refunded: number
    amountConfirmed: number
    currency: string
  }
  stages: StageReport[]
}

export type Tasks = Partial<
  Record<'review' | 'validate' | 'corrections' | 'sheets' | 'approvals' | 'notifications', number>
>

export function useOverview(competitionId: string, enabled: boolean) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...REPORTS_KEY, 'overview', competitionId],
    queryFn: () =>
      session.api.get<Overview>('/admin/reports/overview', {
        query: competitionId ? { competitionId } : {},
      }),
    enabled,
  })
}

export function useTasks(enabled: boolean) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...REPORTS_KEY, 'tasks'],
    queryFn: () => session.api.get<Tasks>('/admin/reports/tasks'),
    enabled,
    refetchInterval: 60_000,
  })
}

export interface ReportExport {
  id: string
  kind: ReportKind
  kindLabel: string
  format: ReportFormat
  formatLabel: string
  competition: string | null
  stage: string | null
  status: ExportStatus
  statusLabel: string
  rows: number | null
  bytes: number | null
  fileName: string | null
  error: string | null
  createdAt: string | null
  expiresAt: string | null
  downloadedAt: string | null
}

export function useExports() {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...REPORTS_KEY, 'exports'],
    queryFn: () =>
      session.api.get<{ data: ReportExport[] }>('/admin/report-exports').then((r) => r.data),
    refetchInterval: (q) =>
      q.state.data?.some((e) => e.status === 'queued' || e.status === 'running') ? 2000 : false,
  })
}

const number = new Intl.NumberFormat('fr-FR')
export const fmtNumber = (n: number) => number.format(n)
export const fmtScore = (n: number | null) =>
  n === null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(n)
export const fmtMoney = (n: number, currency: string) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(
    n
  )
